import { type FC, useMemo } from "react";
import DeliveryInformation from "~/features/orders/orderDetails/components/ShipmentDetails/DeliveryInformation";
import PackageTable from "~/features/orders/orderDetails/components/ShipmentDetails/PackageTable";
import { type ShipmentLineData } from "~/domains/orders/types/models";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { type DetailedOrderData, type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";

interface ShipmentDetailsProps {
  data: DetailedOrderData;
}

const ShipmentDetails: FC<ShipmentDetailsProps> = ({ data }) => {
  const { t } = useTranslation();
  const deliveryEntries = data.deliveryEntries;
  const shipmentInformationDataWrapper = useMemo(() => {
    const orderDetailsData: Dict<OrderLineItemData[]> = {};
    if (data.deliveryEntries && data.deliveryEntries.length > 0) {
      data.deliveryEntries.forEach((deliveryEntry) => {
        Object.values(deliveryEntry.packages).forEach((shipmentPackages: ShipmentLineData[]) => {
          Object.values(shipmentPackages).forEach((shipmentPackage) => {
            // SCHAEFF-16777 delivery with no handling unit, only has one package, so packageID can be the deliveryNumber
            const packageId =
              shipmentPackage.externalHandlingUnitId != ""
                ? shipmentPackage.externalHandlingUnitId
                : deliveryEntry.deliveryNumber;
            if (!orderDetailsData[packageId]) {
              orderDetailsData[packageId] = [];
            }
            // According to SCHAEFF-14361 not all order lines are merged, so we still need to keep the "lineNumber:scheduleLineNumber" format as a fallback
            const orderEntries: OrderLineItemData[] | undefined = data.products.filter((entry) =>
              entry.lineNo.includes(":")
                ? entry.lineNo === `${shipmentPackage.lineNumber}:${shipmentPackage.scheduleLineNumber}`
                : entry.lineNo === `${shipmentPackage.lineNumber}`,
            );
            if (orderEntries && orderEntries.length > 0) {
              orderEntries.forEach((orderEntry) => {
                // If an order entry with the same lineNo has already been added in this package, this means the 2 order entries have been merged
                // So we don't need to add it another time
                const existingOrderEntry = orderDetailsData[packageId].find(
                  (entry) => entry.lineNo === orderEntry.lineNo,
                );
                if (!existingOrderEntry) {
                  orderDetailsData[packageId].push(orderEntry);
                }
              });
            }
          });
        });
      });
    }
    return {
      orderDetailsData: orderDetailsData,
    };
  }, [data]);

  const constainsSurcharge = useMemo(() => {
    return data.products.length > 0 && data.products.some((lineItem) => lineItem.netPrice.surcharge > 0);
  }, [data]);

  return (
    <>
      <div className="d-flex flex-justify-between item-shippment-title-holder mt-30">
        <p className="m-0 headline-small scha__screen-only">
          {t("cartPage.header.shipmentDetails", { count: data.deliveryEntries.length })}
        </p>
        <p className="m-0 headline-small scha__print-only">{t("orderDetailsPage.shipmentDetails.title")}</p>
      </div>

      {deliveryEntries.map((deliveryEntry, index) => {
        return (
          <>
            <DeliveryInformation data={deliveryEntry} freightPayment={data.freightPayment} />
            <PackageTable
              deliveryEntryData={deliveryEntry}
              shipmentInformationDataWrapper={shipmentInformationDataWrapper}
            />
          </>
        );
      })}
      {<IncludesSurchargeDisclaimer hasSurcharge={constainsSurcharge} className={"mt-30"} />}
    </>
  );
};

export default ShipmentDetails;
